import { cn } from '../../lib/cn.js'
import { Card } from '../ui/Card.js'
import { Text } from '../ui/Typography.js'
import { Skeleton } from '../ui/Skeleton.js'

export interface StatCardProps {
  label: React.ReactNode
  value: React.ReactNode
  /**
   * What it is measured against — "+6 on yesterday".
   *
   * Optional, and null rather than "+0" when there is nothing to compare to:
   * *"eleven bookings means two different things depending on whether yesterday
   * was four or forty"*, and "+0" reads as a measured result rather than as an
   * absence of one.
   */
  comparison?: React.ReactNode
  loading?: boolean
  /**
   * Show me the ones this is counting.
   *
   * Every list screen puts four figures over a table whose tabs filter to
   * exactly those states — *"Suspended 3"* directly above a **Suspended**
   * tab — and the figure was inert. Reading "3 suspended" and then hunting
   * for the tab that shows them is two acts where the screen has already
   * put them next to each other.
   *
   * Given, the card becomes a button and says so. Left out, it stays a
   * figure: a card that looks pressable and is not is worse than a plain
   * one, which is why this is opt-in rather than a default.
   */
  onSelect?: () => void
  /** Whether the table is already narrowed to this. */
  selected?: boolean
}

/** One of the figures across the top of the overview. */
export function StatCard({ label, value, comparison, loading, onSelect, selected }: StatCardProps) {
  const body = (
    <>
      <Text variant="caption">{label}</Text>
      {loading ? (
        <Skeleton className="mt-2 h-8 w-20" />
      ) : (
        <Text as="p" variant="display" className="mt-1 tabular-nums">
          {value}
        </Text>
      )}
      {!loading && comparison && (
        <Text variant="caption" className="mt-1">
          {comparison}
        </Text>
      )}
    </>
  )

  if (!onSelect) {
    return <Card padding="sm">{body}</Card>
  }

  return (
    <Card
      asChild
      padding="sm"
      className={cn(
        'hover:border-hairline-strong cursor-pointer text-left transition-colors',
        'focus-visible:ring-coral-fill/30 outline-none focus-visible:ring-2',
        /* The one the table is already showing, marked — or pressing it
           twice reads as having done nothing. */
        selected && 'border-coral-fill bg-coral-soft/40',
      )}
    >
      <button type="button" onClick={onSelect} aria-pressed={selected}>
        {body}
      </button>
    </Card>
  )
}
